import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  Actividade,
  ActividadeRequest,
  ApiError,
  CondLimiteEnum,
  ProdutoDocs,
  ProdutoDocsRequest,
  ProdutoService, RefundPolicyEnum,
  TipoIdEnum,
} from '../../../slidein_api';
import { fileLink, msgAPIError } from '../../helpers';
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  useMantineReactTable,
} from 'mantine-react-table';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  FileInput,
  Group,
  Modal,
  NumberInput,
  Select,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconCircleCheck,
  IconCircleX,
  IconCurrencyEuro,
  IconDeviceFloppy,
  IconEdit,
  IconFiles,
  IconIdBadge2,
  IconMessageCircle,
  IconNotes,
  IconPackage,
  IconPackages,
  IconPdf,
  IconPlus,
  IconQuestionMark, IconReportMoney,
  IconTrash,
  IconTrashX,
  IconUpload,
  IconUser,
  IconUsersGroup,
  IconX,
} from '@tabler/icons-react';
import { MRT_Localization_PT } from 'mantine-react-table/locales/pt';
import { useForm, yupResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../../user-auth/SlideInAuthContext';
import { checkPermission, MASTER } from '../../../components/CheckPermission';

const EMPTYACTIVIDADE: ActividadeRequest = {
  descricao: '',
  nome_interno: '',
  titulo_cliente: '',
  valor: undefined,
  campanha: '',
  activo: true,
  cancelavel: false,
  inicial: false,
  inscricao_previa: false,
  brevemente: false,
  msg_metodo_inscricao: '',
  refund_policy: RefundPolicyEnum.SEM_REEMBOLSO,
};

export function ActividadeManager() {
  const { colaborador } = useAuth();
  const params = useParams();
  const [dialogParams, setDialogParams] = useState<{
    create: boolean;
    visible: boolean;
  }>({
    create: true,
    visible: false,
  });
  const [update, setUpdate] = useState<number>(0);
  const [actividades, setActividades] = useState<Actividade[]>([]);
  const [actividadeDocsEdit, setActividadeDocEdit] = useState<{
    actividade: Actividade;
    docs: ProdutoDocs[];
  }>(undefined);
  const [docsDiag, setDocsDialog] = useState<boolean>(false);
  const [docsDiagAdd, setDocsDialogAdd] = useState<boolean>(false);

  useEffect(() => {
    ProdutoService.produtoActividadeList({ campanha: params.campanhaID }).then(
      (value) => {
        setActividades(value);
      },
      (reason) => msgAPIError(reason)
    );
  }, [update]);

  const actividadeForm = useForm<Actividade | ActividadeRequest>({
    initialValues: EMPTYACTIVIDADE,
    validate: yupResolver(
      Yup.object({
        nome_interno: Yup.string().min(4, 'Tamanho mínimo de 4 caracteres').required('Obrigatório'),
        titulo_cliente: Yup.string().required('Obrigatório'),
        valor: Yup.number().min(0).required('Obrigatório'),
      })
    ),
  });
  const saveActividade = async (actividade: Actividade) => {
    if (dialogParams.create) {
      actividade.campanha = params.campanhaID;
      ProdutoService.produtoActividadeCreate({ requestBody: actividade }).then(
        () => {
          setUpdate(update + 1);
          setDialogParams({ create: true, visible: false });
        },
        (error) => {
          actividadeForm.setErrors(error.body);
        }
      );
    } else {
      ProdutoService.produtoActividadeUpdate({
        id: actividade.id,
        requestBody: actividade,
      }).then(
        () => {
          setUpdate(update + 1);
          setDialogParams({ create: true, visible: false });
          notifications.show({
            title: `Actividade actualizado!`,
            message: `O Actividade ${actividade.nome_interno} foi actualizado.`,
            withCloseButton: true,
            icon: <IconCircleCheck />,
            color: 'green',
          });
        },
        (error) => {
          actividadeForm.setErrors(error.body);
        }
      );
    }
  };

  function criarActividade() {
    actividadeForm.reset();
    setDialogParams({
      create: true,
      visible: true,
    });
  }

  function editActividade(actividade: Actividade) {
    actividadeForm.reset();
    actividadeForm.setValues(actividade);
    setDialogParams({
      create: false,
      visible: true,
    });
  }

  function removeActividade(actividade: Actividade) {
    if (confirm('Confirma que deseja remover o actividade : ' + actividade.nome_interno + ' ?')) {
      ProdutoService.produtoActividadeDestroy({ id: actividade.id }).then(
        () => {
          setUpdate(update + 1);
          notifications.show({
            title: 'Actividade removido!',
            message: `O actividade ${actividade.nome_interno} foi removido.`,
            withCloseButton: true,
            icon: <IconTrashX />,
            color: 'red',
          });
        },
        (reason) => {
          msgAPIError(reason);
        }
      );
    }
  }

  const editActividadeDialog = (
    <Modal
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      size={'auto'}
      opened={dialogParams.visible}
      onClose={() => {
        setDialogParams({ create: false, visible: false });
      }}
      title={dialogParams.create ? 'Criar Actividade' : 'Editar Actividade'}>
      <form onSubmit={actividadeForm.onSubmit(saveActividade)}>
        <TextInput
          label={'Nome Interno'}
          icon={<IconPackage />}
          {...actividadeForm.getInputProps('nome_interno')}
        />
        <Text color={'red'}>{actividadeForm.errors['non_field_errors']}</Text>
        <TextInput
          label={'Titulo Cliente'}
          icon={<IconUser />}
          {...actividadeForm.getInputProps('titulo_cliente')}
        />

        <NumberInput
          placeholder='Valor'
          label='Valor'
          precision={2}
          icon={<IconCurrencyEuro />}
          hideControls
          decimalSeparator=','
          thousandsSeparator='.'
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          formatter={(value) =>
            !Number.isNaN(parseFloat(value))
              ? // eslint-disable-next-line security/detect-unsafe-regex
                `${value} €`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
              : ''
          }
          {...actividadeForm.getInputProps('valor')}
        />
        {!dialogParams.create && (
          <Text color={'red'}>
            Não deve alterar o valor após criado/vendido! Criar um actividade nova e desativar esta!
          </Text>
        )}
           <Select
          label={'Reembolso:'}
          icon={<IconReportMoney />}
          data={Object.keys(RefundPolicyEnum).map((x) => {
            return {
              label: RefundPolicyEnum[x as keyof typeof RefundPolicyEnum],
              value: RefundPolicyEnum[x as keyof typeof RefundPolicyEnum],
            };
          })}
          {...actividadeForm.getInputProps('refund_policy')}
        />

        <NumberInput
          placeholder='Ilimitado'
          label='Unidades Disponiveis(Stock)'
          precision={0}
          description={'Deixar em branco para ser ilimitado.'}
          min={0}
          icon={<IconPackages />}
          thousandsSeparator='.'
          value={actividadeForm.values.unidades !== null ? actividadeForm.values.unidades : ''}
          onChange={(value) => {
            if (value !== '') {
              actividadeForm.setFieldValue('unidades', value);
            } else {
              actividadeForm.setFieldValue('unidades', null);
            }
          }}
        />
        <Select
          label={'Condição:'}
          icon={<IconQuestionMark />}
          data={Object.keys(CondLimiteEnum).map((x) => {
            return {
              label: CondLimiteEnum[x as keyof typeof CondLimiteEnum],
              value: CondLimiteEnum[x as keyof typeof CondLimiteEnum],
            };
          })}
          {...actividadeForm.getInputProps('cond_limite')}
        />

        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label=' Activo, disponivel para compra pelos clientes'
          color='green'
          radius='md'
          size='lg'
          {...actividadeForm.getInputProps('activo', { type: 'checkbox' })}
        />
        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label=' Cancelavel, O cliente pode cancelar se ainda não pagou a actividade'
          color='green'
          radius='md'
          size='lg'
          {...actividadeForm.getInputProps('cancelavel', { type: 'checkbox' })}
        />
        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label='Adicionar automáticamente aos Grupos (na criação)'
          color='green'
          radius='md'
          size='lg'
          {...actividadeForm.getInputProps('inicial', { type: 'checkbox' })}
        />
        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label='Modo Brevemente (Ainda não disponivel para compra)'
          color='green'
          radius='md'
          size='lg'
          {...actividadeForm.getInputProps('brevemente', { type: 'checkbox' })}
        />
        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label='Inscrição via "Outro  método"'
          color='green'
          radius='md'
          size='lg'
          {...actividadeForm.getInputProps('inscricao_previa', { type: 'checkbox' })}
        />
        {actividadeForm.values.inscricao_previa && (
          <TextInput
            label={'Método de Inscrição (Mensagem cliente)'}
            icon={<IconMessageCircle />}
            {...actividadeForm.getInputProps('msg_metodo_inscricao')}
          />
        )}

        <Textarea
          placeholder='Descrição...'
          label='Descrição'
          size='md'
          icon={<IconNotes />}
          autosize={true}
          minRows={2}
          maxRows={4}
          {...actividadeForm.getInputProps('descricao')}
        />
        <NumberInput
          label={'# Ordem de aparencia clientes'}
          min={0}
          placeholder={'Em branco = último.'}
          value={
            actividadeForm.values.ordem_de_aparencia !== null
              ? actividadeForm.values.ordem_de_aparencia
              : ''
          }
          onChange={(value) => {
            if (value !== '') {
              actividadeForm.setFieldValue('ordem_de_aparencia', value);
            } else {
              actividadeForm.setFieldValue('ordem_de_aparencia', null);
            }
          }}
        />
        <Group
          position='right'
          mt='md'>
          <Button
            leftIcon={<IconDeviceFloppy />}
            variant={'outline'}
            type='submit'>
            Guardar
          </Button>
          <Button
            color={'yellow'}
            variant={'outline'}
            leftIcon={<IconX />}
            type='button'
            onClick={() => setDialogParams({ create: false, visible: false })}>
            Cancelar
          </Button>
        </Group>
      </form>
    </Modal>
  );

  //--------------------------------------------------------------
  const docForm = useForm<ProdutoDocsRequest>({
    initialValues: {
      nome: '',
      produto: undefined,
      file: undefined,
    },

    validate: yupResolver(
      Yup.object({
        nome: Yup.string().min(5, 'Tamanho mínimo de 5 caracteres').required('Obrigatório'),
        file: Yup.mixed().required('Nenhum ficheiro selecionado!'),
      })
    ),
  });
  const saveActividadeDoc = async (doc: ProdutoDocsRequest) => {
    doc.produto = actividadeDocsEdit.actividade.id;
    ProdutoService.produtoDocsCreate({
      formData: doc,
    }).then(
      (value) => {
        setUpdate(update + 1);
        setActividadeDocEdit({
          actividade: actividadeDocsEdit.actividade,
          docs: [...actividadeDocsEdit.docs, value],
        });
        setDocsDialogAdd(false);
      },
      (error) => {
        docForm.setErrors(error.body);
      }
    );
  };

  function adicionarActividadeDoc() {
    docForm.reset();
    docForm.values.produto = actividadeDocsEdit.actividade.id;
    setDocsDialogAdd(true);
  }

  const columnsDocs = useMemo<MRT_ColumnDef<ProdutoDocs>[]>(
    () => [
      {
        accessorKey: 'nome',
        header: 'Nome Documento',
      },
      {
        accessorFn: (row) => fileLink(row.nome, row.file),
        header: 'Ficheiro',
      },
    ],
    []
  );

  const editDocsDialog = () => {
    if (actividadeDocsEdit === undefined) {
      return <></>;
    } else {
      return (
        <>
          <Modal
            withCloseButton={true}
            closeOnEscape={false}
            closeOnClickOutside={false}
            size={'xl'}
            title={`Documentos (${actividadeDocsEdit.actividade.nome_interno})`}
            opened={docsDiag && !docsDiagAdd}
            onClose={() => setDocsDialog(false)}>
            <MantineReactTable
              displayColumnDefOptions={{
                'mrt-row-actions': {
                  mantineTableHeadCellProps: {
                    align: 'center',
                  },
                  size: 120,
                },
              }}
              renderTopToolbarCustomActions={() => {
                return (
                  <Box sx={{ display: 'flex', gap: '16px', padding: '4px', flexWrap: 'wrap' }}>
                    <Button
                      disabled={!checkPermission(MASTER, colaborador)}
                      leftIcon={<IconPlus />}
                      variant='outline'
                      onClick={() => {
                        adicionarActividadeDoc();
                      }}>
                      Adicionar Documento
                    </Button>
                  </Box>
                );
              }}
              initialState={{
                density: 'xs',
                showGlobalFilter: true,
                pagination: { pageSize: 15, pageIndex: 0 },
              }}
              enableRowActions={checkPermission(MASTER, colaborador)}
              renderRowActions={({ row, table }) => (
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <Tooltip label='Editar'>
                    <ActionIcon
                      color='blue'
                      onClick={() => table.setEditingRow(row)}>
                      <IconEdit />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label='Remover'>
                    <ActionIcon
                      color='red'
                      onClick={() => {
                        if (confirm('Confirma que deseja remover o documento ?')) {
                          ProdutoService.produtoDocsDestroy({ id: row.original.id }).then(
                            () => {
                              setUpdate(update + 1);
                              setActividadeDocEdit({
                                actividade: actividadeDocsEdit.actividade,
                                docs: actividadeDocsEdit.docs.filter((pa) => {
                                  return pa.id !== row.original.id;
                                }),
                              });

                              notifications.show({
                                title: 'Documento removido!',
                                message: `O documento ${row.original.nome}  foi removido.`,
                                withCloseButton: true,
                                icon: <IconTrashX />,
                                color: 'red',
                              });
                            },
                            (reason) => {
                              msgAPIError(reason);
                            }
                          );
                        }
                      }}>
                      <IconTrashX />
                    </ActionIcon>
                  </Tooltip>
                </Box>
              )}
              columns={columnsDocs}
              data={actividadeDocsEdit.docs}
              positionActionsColumn='first'
              enableFullScreenToggle={false}
              localization={MRT_Localization_PT}
              enableEditing={checkPermission(MASTER, colaborador)}
              enablePagination={false}
              enableFilters={false}
              enableHiding={false}
              enableBottomToolbar={false}
              enableGlobalFilter={false}
              enableDensityToggle={false}
              editDisplayMode={'modal'}
              onEditingRowSave={({ exitEditingMode, row, values }) => {
                ProdutoService.produtoDocsPartialUpdate({
                  id: row.original.id,
                  formData: { nome: values.nome },
                }).then(
                  () => {
                    actividadeDocsEdit.docs[row.index] = values;
                    setActividadeDocEdit({
                      actividade: actividadeDocsEdit.actividade,
                      docs: actividadeDocsEdit.docs,
                    });
                  },
                  (reason: ApiError) => {
                    msgAPIError(reason);
                  }
                );
                exitEditingMode();
              }}
            />
          </Modal>
          <Modal
            withCloseButton={false}
            closeOnEscape={false}
            size={'lg'}
            closeOnClickOutside={false}
            opened={docsDiagAdd}
            onClose={() => {
              setDocsDialogAdd(false);
            }}
            title={'Adicionar Documento'}>
            <form onSubmit={docForm.onSubmit(saveActividadeDoc)}>
              <TextInput
                label={'Nome Documento'}
                icon={<IconPdf />}
                {...docForm.getInputProps('nome')}
              />
              <FileInput
                label={'Ficheiro'}
                multiple={false}
                icon={<IconUpload />}
                {...docForm.getInputProps('file')}
              />

              <Group
                position='right'
                mt='md'>
                <Button
                  variant={'outline'}
                  leftIcon={<IconDeviceFloppy />}
                  type='submit'>
                  Guardar
                </Button>
                <Button
                  color={'yellow'}
                  variant={'outline'}
                  leftIcon={<IconX />}
                  type='button'
                  onClick={() => setDocsDialogAdd(false)}>
                  Cancelar
                </Button>
              </Group>
            </form>
          </Modal>
        </>
      );
    }
  };

  //--------------------------------------------------------------

  function dispaAllGruposActividade(actividade: Actividade) {
    if (
      confirm(
        `Tem a certeza que deseja adicionar o actividade ${actividade.nome_interno} a TODOS os grupos da Campanha?`
      )
    ) {
      ProdutoService.produtoActividadeDisponibilizarATodosOsGruposRetrieve({
        id: actividade.id,
      }).then(
        () => {
          notifications.show({
            title: 'Actividade adicionado ao grupos!',
            message: `O actividade ${actividade.nome_interno} foi adicionado a todo os grupos para selecção.`,
            withCloseButton: true,
            icon: <IconUsersGroup />,
          });
        },
        (reason) => {
          msgAPIError(reason);
        }
      );
    }
  }

  const columns = useMemo<MRT_ColumnDef<Actividade>[]>(
    () => [
      { accessorKey: 'ordem_de_aparencia', header: '#', size: 50, Header: '#' },
      { accessorKey: 'nome_interno', header: 'Nome Interno', size: 200 },
      { accessorKey: 'titulo_cliente', header: 'Título Cliente', size: 200 },
      {
        accessorKey: 'valor',
        header: 'Valor',
        size: 100,
        Cell: (cell) => `${cell.cell.getValue()}€`,
      },
           {
        accessorKey: 'refund_policy',
        header: 'Reembolso',
        size: 100,
      },
      {
        accessorKey: 'unidades',
        header: 'Uni. Disp.',
        size: 100,
        Cell: (cell) => (cell.cell.getValue() !== null ? `${cell.cell.getValue()}` : 'Ilimitado'),
      },
      {
        accessorKey: 'cond_limite',
        header: 'Condição',
        size: 100,
      },
      {
        accessorFn: (row) => `${row.nr_vendidos}` || '-',
        header: 'Vendidos',
        size: 50,
      },
      {
        accessorFn: (row) => `${row.nr_vendidos_e_pagos}` || '-',
        header: 'Reservados(com Pag.)',
        size: 50,
      },

      {
        accessorKey: 'activo',
        header: 'Ativo',
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Activo, disponivel para compra'}>
            <div>Act.</div>
          </Tooltip>
        ),
        size: 50,
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <IconCircleCheck color='green' />
          ) : (
            <IconCircleX color='red' />
          );
        },
      },
      {
        accessorKey: 'cancelavel',
        header: 'Cancelavel',
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Cancelavel (Cliente)'}>
            <div>Cancel</div>
          </Tooltip>
        ),
        size: 50,
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <IconCircleCheck color='green' />
          ) : (
            <IconCircleX color='red' />
          );
        },
      },
      {
        accessorKey: 'inicial',
        header: 'Auto',
        size: 50,
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Na criação de um grupo adicionar automaticamente para escolha'}>
            <div>Auto.</div>
          </Tooltip>
        ),
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <IconCircleCheck color='green' />
          ) : (
            <IconCircleX color='red' />
          );
        },
      },
      {
        accessorKey: 'brevemente',
        header: 'brevemente',
        size: 50,
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Disponivel Brevemente,sem revelar preço'}>
            <div>Brevemente</div>
          </Tooltip>
        ),
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <IconCircleCheck color='green' />
          ) : (
            <IconCircleX color='red' />
          );
        },
      },
      {
        accessorKey: 'inscricao_previa',
        csv: true,
        header: 'Incrição Previa',
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Incrição Previa,via "Outro  método".'}>
            <div>Inscrição</div>
          </Tooltip>
        ),
        size: 50,
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <Tooltip
              withinPortal={true}
              label={cell.row.original.msg_metodo_inscricao}>
              <IconCircleCheck color='green' />
            </Tooltip>
          ) : (
            <IconCircleX color='red' />
          );
        },
      },

      {
        accessorKey: 'docs',
        csv: false,
        header: 'Documentos',
        Header: 'Documentos',
        Cell: (cell) => (
          <Button
            leftIcon={<IconFiles />}
            onClick={() => {
              setActividadeDocEdit({ actividade: cell.row.original, docs: cell.row.original.docs });
              setDocsDialog(true);
            }}
            variant='outline'>
            {cell.row.original.docs.length} Docs
          </Button>
        ),
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: actividades,
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Button
          disabled={!checkPermission(MASTER, colaborador)}
          leftIcon={<IconPlus />}
          variant='outline'
          onClick={criarActividade}>
          Adicionar Actividade
        </Button>
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
      </>
    ),
    // renderTopToolbarCustomActions: () => (
    //   <Button
    //     disabled={!actividades.length|| !checkPermission(MASTER, colaborador)}
    //     leftIcon={<IconCsv />}
    //     rightIcon={<IconDownload />}
    //     onClick={exportCSV}
    //     variant='outline'
    //   />
    // ),
    initialState: {
      density: 'xs',
    },
    enableRowActions: checkPermission(MASTER, colaborador),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '4px' }}>
        <Tooltip label='Editar Actividade'>
          <ActionIcon onClick={() => editActividade(row.original)}>
            <IconEdit />
          </ActionIcon>
        </Tooltip>
        <Tooltip label='Remover Produto'>
          <ActionIcon
            color='red'
            onClick={() => removeActividade(row.original)}>
            <IconTrash />
          </ActionIcon>
        </Tooltip>
        <Tooltip label='Adicionar como opção a todos Grupos da Campanha'>
          <ActionIcon
            color='yellow'
            onClick={() => dispaAllGruposActividade(row.original)}>
            <IconUsersGroup />
          </ActionIcon>
        </Tooltip>
      </Box>
    ),

    enablePagination: false,
    enableColumnResizing: false,
    enableColumnFilters: false,
    enableBottomToolbar: false,
    enableGlobalFilter: false,
    enableSorting: true,
    positionActionsColumn: 'first',
    enableFullScreenToggle: false,
    localization: MRT_Localization_PT,
  });

  return (
    <Box>
      <MantineReactTable table={table} />
      {editActividadeDialog}
      {editDocsDialog()}
    </Box>
  );
}
